const KOLKATA_TIME_ZONE = 'Asia/Kolkata';

export interface TransactionDateParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

const TRANSACTION_DATE_PATTERN =
  /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?/;

const getKolkataDateTimeParts = (date: Date = new Date()): TransactionDateParts => {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: KOLKATA_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = part.value;
    }
    return acc;
  }, {});

  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    hour: Number(parts.hour),
    minute: Number(parts.minute),
    second: Number(parts.second),
  };
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const parseTransactionDateParts = (dateString: string | undefined | null): TransactionDateParts => {
  if (!dateString) {
    return getKolkataDateTimeParts();
  }

  const match = TRANSACTION_DATE_PATTERN.exec(dateString);
  if (!match) {
    return getKolkataDateTimeParts();
  }

  return {
    year: Number(match[1]),
    month: Number(match[2]),
    day: Number(match[3]),
    hour: Number(match[4] ?? 0),
    minute: Number(match[5] ?? 0),
    second: Number(match[6] ?? 0),
  };
};

export const formatTransactionDateInputValue = (dateString: string | undefined | null): string => {
  const parts = parseTransactionDateParts(dateString);
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

export const getTodayKolkataDateInputValue = (): string => {
  const parts = getKolkataDateTimeParts();
  return `${parts.year}-${pad2(parts.month)}-${pad2(parts.day)}`;
};

export const getCurrentKolkataYearMonth = (): { year: number; month: number } => {
  const parts = getKolkataDateTimeParts();
  return { year: parts.year, month: parts.month };
};

export const buildTransactionDateTime = (
  dateInputValue: string,
  timeSource?: string | null,
): string => {
  const safeDateInputValue = dateInputValue || getTodayKolkataDateInputValue();
  const timeParts = timeSource
    ? parseTransactionDateParts(timeSource)
    : getKolkataDateTimeParts();

  return `${safeDateInputValue}T${pad2(timeParts.hour)}:${pad2(timeParts.minute)}:${pad2(timeParts.second)}`;
};

export const compareTransactionDateDesc = (
  a: string | undefined | null,
  b: string | undefined | null,
): number => {
  const aParts = parseTransactionDateParts(a);
  const bParts = parseTransactionDateParts(b);
  const aComparable = `${aParts.year}${pad2(aParts.month)}${pad2(aParts.day)}${pad2(aParts.hour)}${pad2(aParts.minute)}${pad2(aParts.second)}`;
  const bComparable = `${bParts.year}${pad2(bParts.month)}${pad2(bParts.day)}${pad2(bParts.hour)}${pad2(bParts.minute)}${pad2(bParts.second)}`;
  return bComparable.localeCompare(aComparable);
};

export const formatTransactionMonthYear = (dateString: string): string => {
  const parts = parseTransactionDateParts(dateString);
  const monthName = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2000, parts.month - 1, 1)));
  return `${monthName} ${parts.year}`;
};

export const formatTransactionShortDate = (dateString: string): string => {
  const parts = parseTransactionDateParts(dateString);
  const now = getKolkataDateTimeParts();
  const today = Date.UTC(now.year, now.month - 1, now.day);
  const target = Date.UTC(parts.year, parts.month - 1, parts.day);
  const daysDifference = Math.round((today - target) / 86_400_000);

  if (daysDifference === 0) {
    return 'Today';
  }
  if (daysDifference === 1) {
    return 'Yesterday';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day)));
};

export const formatTransactionHeaderDate = (dateString: string): string => {
  const parts = parseTransactionDateParts(dateString);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(parts.year, parts.month - 1, parts.day))).replace(/,/g, '');
};

export const formatTransactionManualDescriptionDate = (dateString: string): string => {
  const parts = parseTransactionDateParts(dateString);
  return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year} ${pad2(parts.hour)}:${pad2(parts.minute)}:${pad2(parts.second)}`;
};

export const formatInstantDate = (
  dateString: string | undefined | null,
  includeTime: boolean = false,
): string => {
  if (!dateString) {
    return 'Invalid Date';
  }

  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) {
    return 'Invalid Date';
  }

  const options: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };
  if (includeTime) {
    options.hour = 'numeric';
    options.minute = '2-digit';
    options.hour12 = true;
  }

  return new Intl.DateTimeFormat('en-US', options).format(date);
};
